import React, { PropsWithChildren } from 'react';
import { BaseProvider } from '@telus-uds/components-web';
import alliumTheme from '@telus-uds/theme-allium';

/**
 * Host-side port of `varizeTheme` from simplify-mfe-components
 * `packages/shared/src/dark-mode`. That package is private to the MFE repo, so
 * the host cannot import it; the dark *values* still come from there. The
 * navbar MFE (always mounted by App) loads `dark-vars.css` and `tokens.css`
 * onto `:root`, and each `var()` here falls back to its light hex when they are
 * absent, so a missing navbar degrades to the light theme rather than breaking.
 *
 * Keep the variable naming in step with `colorMap.ts` in that repo.
 */

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

// Non-hex palette strings that still need to flip; mirrors RAW_OVERRIDES.
const RAW_OVERRIDES = new Set(['rgba(244, 244, 247, 0.97)']);

const toVar = (hex: string): string =>
  `var(--uds-c-${hex.slice(1).toLowerCase()}, ${hex.toLowerCase()})`;

const rawVarName = (raw: string): string =>
  `--uds-r-${raw.replace(/[^a-z0-9]/gi, '').toLowerCase()}`;

/**
 * Deep-clone a theme, replacing every value that is exactly a 6-digit hex with
 * its `var(--uds-c-*)` reference. Hex substrings inside larger strings (SVG
 * data URIs, gradients) and functions pass through untouched.
 */
export const varizeTheme = (value: unknown): unknown => {
  if (typeof value === 'string') {
    if (HEX_RE.test(value)) return toVar(value);
    if (RAW_OVERRIDES.has(value)) return `var(${rawVarName(value)}, ${value})`;
    return value;
  }
  if (Array.isArray(value)) return value.map(varizeTheme);
  if (value && typeof value === 'object') {
    const proto: unknown = Object.getPrototypeOf(value);
    if (proto === Object.prototype || proto === null) {
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(value)) out[k] = varizeTheme(v);
      return out;
    }
  }
  return value;
};

export const darkAwareAlliumTheme = varizeTheme(alliumTheme);

/**
 * UDS provider whose colors follow `<html data-theme>`. Wrap host-rendered
 * pages that support dark mode in it; everything else stays on the plain
 * allium theme from main.tsx.
 */
export const DarkAwareUdsProvider = ({ children }: PropsWithChildren<{}>) => (
  <BaseProvider defaultTheme={darkAwareAlliumTheme as never}>
    {children}
  </BaseProvider>
);
